import { FunctionComponent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import {
  Container,
  Button,
  Row,
  Form,
  Breadcrumb,
  Spinner,
  Alert,
} from 'react-bootstrap'

import * as api from './api.client'
import styles from './style.module.scss'
import { HospitalDoctor } from './types'
import { removeAlphabeticCharacteres } from '../../utilities/string.util'
import { ReactComponent as SaveIcon } from '../../assets/icons/save.svg'
import { AppMessage } from '../../utilities/messages'
import { formatCpf, validateCpf } from '../../utilities/cpf.util'
import { formatPhoneNumber } from '../../utilities/phoneNumber.util'

const emptyDoctor: HospitalDoctor = {
  id: 0,
  name: '',
  email: '',
  cpf: '',
  phoneNumber: '',
}

const EditDoctor: FunctionComponent = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [doctor, setDoctor] = useState<HospitalDoctor>(emptyDoctor)
  const [loading, setLoading] = useState(true)
  const [showFormErrors, setShowFormErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [appMessage, setAppMessage] = useState<AppMessage>({ show: false })

  useEffect(() => {
    const loadDoctor = async () => {
      try {
        const doctors = await api.getDoctors()
        const found = doctors.find(
          (item) => item.id === Number.parseInt(id as string)
        )

        if (!found) {
          setAppMessage({
            show: true,
            success: false,
            message: 'Médico não encontrado.',
          })
          return
        }

        setDoctor(found)
      } catch (error) {
        setAppMessage({
          show: true,
          success: false,
          message: `Erro ao carregar o médico: ${(error as any).messages.join(
            ', '
          )}`,
        })
      } finally {
        setLoading(false)
      }
    }

    loadDoctor()
  }, [id])

  const updateDoctor = async (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formIsValid = event.currentTarget.checkValidity()

    if (!formIsValid) {
      setShowFormErrors(true)
      return
    }

    setSaving(true)

    try {
      await api.updateDoctor(doctor.id, {
        name: doctor.name,
        email: doctor.email,
        cpf: removeAlphabeticCharacteres(doctor.cpf),
        phoneNumber: removeAlphabeticCharacteres(doctor.phoneNumber),
      })

      navigate(`/doctors`, {
        state: {
          doctorUpdated: true,
        },
      })
    } catch (error) {
      setAppMessage({
        show: true,
        success: false,
        message: `Erro ao atualizar o médico: ${(error as any).messages.join(
          ', '
        )}`,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleFormChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = target

    setDoctor({ ...doctor, [name]: type === 'checkbox' ? checked : value })
  }

  if (loading) {
    return (
      <Container fluid className={styles.container}>
        <Spinner animation="border" size="sm" />
      </Container>
    )
  }

  return (
    <Container fluid className={styles.container}>
      {appMessage.show && (
        <Alert
          variant="danger"
          style={{ marginTop: 20 }}
          onClose={() => setAppMessage({ show: false })}
          dismissible
        >
          {appMessage.message}
        </Alert>
      )}

      <Row>
        <Breadcrumb>
          <Breadcrumb.Item onClick={() => navigate('/doctors')}>
            Médicos
          </Breadcrumb.Item>
          <Breadcrumb.Item active>Editar médico</Breadcrumb.Item>
        </Breadcrumb>
      </Row>

      <Form noValidate validated={showFormErrors} onSubmit={updateDoctor}>
        <Form.Group className="mb-3" controlId="formNome">
          <Form.Label>Nome</Form.Label>
          <Form.Control
            type="text"
            placeholder="Nome"
            name="name"
            value={doctor.name}
            onChange={handleFormChange}
            required
          />
          <Form.Control.Feedback type="invalid">
            Informe o nome do médico.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formEmail">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="Email"
            name="email"
            value={doctor.email}
            onChange={handleFormChange}
            required
          />
          <Form.Control.Feedback type="invalid">
            Informe o email do médico.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formCPF">
          <Form.Label>CPF</Form.Label>
          <Form.Control
            type="text"
            placeholder="CPF"
            name="cpf"
            value={formatCpf(doctor.cpf)}
            onChange={handleFormChange}
            minLength={14}
            maxLength={14}
            required
            isValid={validateCpf(formatCpf(doctor.cpf))}
          />
          <Form.Control.Feedback type="invalid">
            Informe um CPF válido.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formPhoneNumber">
          <Form.Label>Telefone</Form.Label>
          <Form.Control
            type="text"
            placeholder="Telefone"
            name="phoneNumber"
            value={formatPhoneNumber(doctor.phoneNumber)}
            onChange={handleFormChange}
            minLength={15}
            maxLength={15}
            required
          />
        </Form.Group>

        <Button variant="primary" type="submit">
          {saving && (
            <div>
              <SaveIcon />{' '}
              <span className={styles.txtButtonAddHospital}>Salvar</span>{' '}
              <Spinner animation="border" size="sm" />{' '}
            </div>
          )}
          {!saving && (
            <div>
              <SaveIcon />{' '}
              <span className={styles.txtButtonAddHospital}>Salvar</span>
            </div>
          )}
        </Button>
      </Form>
    </Container>
  )
}

export default EditDoctor
